import { useState, useEffect, useRef, useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { mobxInjectSelect } from '../../../../common/utils';
import { PageHeader } from '../../../shared-components/PageHeader/PageHeader';
import { ActionBar } from '../../../shared-components/ActionBar/ActionBar';
import {
  PermissionMap,
  UserData,
  UserInfo,
  UserSettings,
  UserOrganizationSettings,
} from '../../../types';

import { Toast } from '../../../shared-components/Toast/Toast';
import settingsDiff from '../../../utils/settingsDiff';
import avatarNoIconBG from '../../../images/test_avatar_noicon_bg.png';
import { CtaButton } from '../../../shared-components/CtaButton/CtaButton';
import { getUserPermissions } from './UserSettingsForm/UserSettings';

import UserSettingsForm from './UserSettingsForm';
import UserInfoForm from './UserInfoForm';
import {
  Api,
  Devices,
  CustomDirectories,
  Pager,
  ProductRoles,
  RemoveUser,
  ResetPassword,
  UnlockUser,
  UserPermissions,
} from './views';

import styles from './index.module.css';
import ConfirmEmailChangeModal from 'common/components/ConfirmEmailChange/ConfirmEmailChangeModal';
import TCClient from 'client';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';
import { loadOrganizationPermissions } from 'admin/store/admin/thunk';
import { useGetUserAccountDataQuery } from 'redux-stores/admin/usersApi';
import { DotsIndicator } from 'common/components';

const { setUsersPageView } = actions;

type View =
  | 'API'
  | 'Devices'
  | 'Custom Directories'
  | 'Pager'
  | 'Product Roles'
  | 'Remove User'
  | 'Reset Password'
  | 'Unlock User'
  | 'User Permissions';

export type FormFields = {
  displayName: string;
  emailAddress: string;
};

const permissionedTabs = [
  'Reset Password',
  'Unlock User',
  'Pager',
  'API',
  'Remove User',
  'Devices',
  'Custom Directories',
  'User Permissions',
  'Product Roles',
] as const;

type PermissionedTab = (typeof permissionedTabs)[number];

const tabToPermission: { [k in PermissionedTab]: string } = {
  'Custom Directories': 'assign_search_groups',
  'Product Roles': 'available_roles',
  'Remove User': 'delete_user',
  'Reset Password': 'reset_password',
  'Unlock User': 'isLocked',
  'User Permissions': 'display_roles',
  API: 'api',
  Devices: 'devices',
  Pager: 'pager',
};

type MobxProps = {
  allowOneTimePasswordLogin: boolean;
  featureFlags: Record<string, unknown>;
  getURLParams: () => Record<string, string>;
  isNewUserSettingsLayoutEnabled: boolean;
  isProductSettingsAdmin: boolean;
  isPersonaAdminFeatureFlagEnabled: boolean;
  isCustomAttributesFeatureFlagEnabled: boolean;
  isLocationsEnabled: boolean;
  isEditableEmailAddressEnabled: boolean;
};

const filterUserData = (userData: UserData, currentOrganizationId: string) => {
  const orgSettings = userData.organizationSettings.find(
    (org) => org.organizationId === currentOrganizationId
  );
  return {
    displayName: userData.displayName,
    title: orgSettings?.title || '',
    department: orgSettings?.department || '',
    emailAddress: userData.emailAddresses[0]?.emailAddress || '',
  };
};

const User = ({
  allowOneTimePasswordLogin,
  featureFlags,
  getURLParams,
  isNewUserSettingsLayoutEnabled,
  isProductSettingsAdmin,
  isPersonaAdminFeatureFlagEnabled,
  isCustomAttributesFeatureFlagEnabled,
  isLocationsEnabled,
  isEditableEmailAddressEnabled,
}: MobxProps) => {
  const dispatch = useAppDispatch();
  const selectedUser = useAppSelector((state) => state.admin.selectedUser);
  const selectedOrganization = useAppSelector((state) => state.admin.selectedOrganization);
  const orgLevelSettings = useAppSelector((state) => state.orgSettings.settings);

  const headerRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<View | null>(null);
  const [tabs, setTabs] = useState<{ label: View }[]>([]);

  const [userData, setUserData] = useState<UserData>();

  const [userInfo, setUserInfo] = useState<UserInfo>({
    displayName: selectedUser.displayName,
    title: selectedUser.title,
    department: selectedUser.department,
    emailAddress: userData?.emailAddresses[0]?.emailAddress || '',
  });
  const [userInfoCopy, setUserInfoCopy] = useState<UserInfo>(userInfo);
  const [unlinkPhoneNumber, setUnlinkPhoneNumber] = useState('');

  const [userSettings, setUserSettings] = useState<UserSettings>();
  const [userSettingsCopy, setUserSettingsCopy] = useState<UserSettings>();
  const [userPermissions, setUserPermissions] = useState<PermissionMap>();
  const [userPermissionsCopy, setUserPermissionsCopy] = useState<PermissionMap>();

  const [isSuccessToastOpen, setIsSuccessToastOpen] = useState(false);
  const [isFailureToastOpen, setIsFailureToastOpen] = useState(false);
  const [failureToastMessage, setFailureToastMessage] = useState<string | undefined>();
  const [isConfirmEmailModalOpen, setIsConfirmEmailModalOpen] = useState(false);

  const [orgSettings, setOrgSettings] = useState<UserOrganizationSettings>();

  const urlParams = getURLParams();
  const isScimSettingEnabled = !!orgLevelSettings?.scimIntegrationSetting;

  const isPersonaBasedManagementAllowed =
    isPersonaAdminFeatureFlagEnabled &&
    !!orgLevelSettings.personaManagement &&
    orgLevelSettings.personaManagement !== 'off';

  const closeView = () => {
    setView(null);
    setSelectedTabIndex(undefined);
  };

  const currentTab = (v: View | null): JSX.Element => {
    switch (v) {
      case 'API':
        return <Api onClose={closeView} />;
      case 'Custom Directories':
        return <CustomDirectories onClose={closeView} />;
      case 'Devices':
        return <Devices onClose={closeView} />;
      case 'Pager':
        return <Pager onClose={closeView} />;
      case 'Product Roles':
        return <ProductRoles onClose={closeView} />;
      case 'Remove User':
        return <RemoveUser onClose={closeView} />;
      case 'Reset Password':
        return userData ? (
          <ResetPassword onClose={closeView} email={userData?.emailAddresses[0]?.emailAddress} />
        ) : (
          <></>
        );
      case 'Unlock User':
        return (
          <UnlockUser
            onClose={closeView}
            isLocked={userData?.isLocked}
            userToken={selectedUser.id}
          />
        );
      case 'User Permissions':
        return (
          <UserPermissions
            onClose={closeView}
            isPersonaMappingExcluded={userSettings?.personaMappingExclusion}
            isPersonaBasedManagementAllowed={isPersonaBasedManagementAllowed}
          />
        );
      default:
        return <></>;
    }
  };

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>();
  const {
    data: userAccountData,
    isLoading: isUserAccountDataLoading,
    error: userAccountDataError,
  } = useGetUserAccountDataQuery(
    {
      userId: selectedUser.id,
      organizationId: selectedOrganization.token,
      userInfo: {
        displayName: selectedUser.displayName,
        title: selectedUser.title,
        department: selectedUser.department,
        emailAddress:
          typeof selectedUser.emails === 'string'
            ? selectedUser.emails
            : selectedUser.emails?.[0]?.address || '',
      },
      isLocationsEnabled,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<FormFields>({
    values: {
      displayName: userAccountData?.displayName || '',
      emailAddress: userAccountData?.emailAddresses[0]?.emailAddress || '',
    },
  });

  useEffect(() => {
    async function unlinkPhone() {
      await TCClient.adminUsers.unlinkPhoneNumber({
        userId: selectedUser.id,
        organizationId: selectedOrganization.token,
        phoneNumber: unlinkPhoneNumber,
      });
      setUnlinkPhoneNumber('');
    }
    if (unlinkPhoneNumber) {
      unlinkPhone();
    }
    if (userAccountData && !isUserAccountDataLoading && !userAccountDataError) {
      setUserData({
        ...userAccountData,
      });
      const userInfo = filterUserData(userAccountData, selectedOrganization.token);
      setUserInfo({ ...userInfo });
      setUserInfoCopy({ ...userInfo });
    }
  }, [
    unlinkPhoneNumber,
    selectedOrganization.token,
    selectedUser.id,
    userAccountData,
    isUserAccountDataLoading,
    userAccountDataError,
  ]);

  useEffect(() => {
    const userId = TCClient.currentUser.id;
    const getUserPermissions = async () => {
      const user_data = (await TCClient.adminUsers.getAccountSettings({
        userId,
        organizationId: selectedOrganization.token,
      })) as UserData;

      const userPermissions =
        user_data.permissions.organization.find(
          (o) => o.organizationId === selectedOrganization.token
        )?.permission ?? [];
      const productPermissions = user_data.permissions.product.permission || [];

      const _tabs: { label: View }[] = permissionedTabs
        .filter((t) => {
          if (t === 'Unlock User') {
            return (
              (orgSettings?.multiFactorAuthSetting !== 'off' &&
                (featureFlags?.MFA || urlParams.allowMfa)) ||
              (allowOneTimePasswordLogin && orgLevelSettings.otpAllowConfigure)
            );
          }
          if (t === 'User Permissions') return !isNewUserSettingsLayoutEnabled;
          const permission = tabToPermission[t as PermissionedTab];
          const userPermission = userPermissions.includes(permission);
          const productPermission = productPermissions.includes(permission);
          if (t === 'Devices') return true;
          return productPermission || userPermission;
        })
        .map((t) => ({ label: t }));
      setTabs(_tabs);
    };

    getUserPermissions();
  }, [
    selectedOrganization.token,
    orgSettings,
    urlParams,
    featureFlags,
    allowOneTimePasswordLogin,
    orgLevelSettings.otpAllowConfigure,
    isNewUserSettingsLayoutEnabled,
  ]);

  useEffect(() => {
    const onScroll = () => {
      if (headerRef.current && tabRef.current) {
        const headerBounds = headerRef.current.getBoundingClientRect();
        const navBounds = tabRef.current.getBoundingClientRect();
        if (headerBounds.bottom > navBounds.top) {
          headerRef.current.setAttribute('sticky', 'sticky');
        } else {
          headerRef.current.removeAttribute('sticky');
        }
      }
    };

    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const focusFirstErrorField = useCallback(() => {
    const firstErrorField = Object.keys(errors).reduce(
      (field: string | null, currentField: string) => {
        return field || (errors[currentField as keyof FormFields] ? currentField : null);
      },
      null
    );

    if (firstErrorField) {
      setFocus(firstErrorField as keyof FormFields);
    }
  }, [errors, setFocus]);

  const performSaveProfile = async () => {
    const userInfoDiff = settingsDiff(userInfo, userInfoCopy);
    const userSettingsDiff =
      userSettings && userSettingsCopy && settingsDiff(userSettings, userSettingsCopy);
    const userPermissionsDiff =
      userPermissions && userPermissionsCopy && settingsDiff(userPermissions, userPermissionsCopy);

    try {
      if (Object.keys(userInfoDiff).length > 0) {
        await TCClient.adminUsers.updateUserInfo({
          userInfoDiff,
          orgToken: selectedOrganization.token,
          userId: selectedUser.id,
        });

        const emailWasChanged = 'emailAddress' in userInfoDiff;
        if (emailWasChanged && userInfoDiff.emailAddress) {
          const updatedAccountSettings = (await TCClient.adminUsers.getAccountSettings({
            userId: selectedUser.id,
            organizationId: selectedOrganization.token,
          })) as UserData;
          const newEmailInResponse = updatedAccountSettings?.emailAddresses?.[0]?.emailAddress;
          if (newEmailInResponse !== userInfo.emailAddress) {
            setUserInfo(userInfoCopy);
            setUserInfoCopy(userInfoCopy);
            setFailureToastMessage(
              'The email address could not be updated. Please try again or contact support.'
            );
            setIsFailureToastOpen(true);
            return;
          }
        }

        setUserInfoCopy(userInfo);
      }
      if (userSettingsDiff && Object.keys(userSettingsDiff).length > 0) {
        await TCClient.adminUsers.updateUserSettings({
          changedUserSettings: userSettingsDiff as UserSettings,
          orgToken: selectedOrganization.token,
          userId: selectedUser.id,
        });
        const user_settings = (await TCClient.adminUsers.getUserSettings({
          userId: selectedUser.id,
          organizationId: selectedOrganization.token,
        })) as UserSettings;
        setUserSettings(user_settings);
        setUserSettingsCopy(user_settings);
      }
      if (userPermissionsDiff && Object.keys(userPermissionsDiff).length > 0) {
        await TCClient.adminUsers.updateUserPermissionRoles({
          orgId: selectedOrganization.token,
          userId: selectedUser.id,
          diffPermissions: userPermissionsCopy,
          permissions: userPermissions,
        });
        const permissions = await getUserPermissions(
          selectedOrganization.token,
          selectedUser.id,
          orgLevelSettings
        );
        setUserPermissions(permissions);
        setUserPermissionsCopy(permissions);
      }
      if (TCClient.currentUser.id === selectedUser.id) {
        await loadOrganizationPermissions(dispatch, selectedOrganization.token);
      }
      setIsSuccessToastOpen(true);
    } catch (e) {
      setFailureToastMessage(undefined);
      setIsFailureToastOpen(true);
    }
  };

  const saveProfile = async () => {
    const emailChanged = userInfo.emailAddress !== userInfoCopy.emailAddress;
    if (emailChanged && isEditableEmailAddressEnabled) {
      setIsConfirmEmailModalOpen(true);
      return;
    }
    await performSaveProfile();
  };

  const cancelSave = async () => {
    setUserInfo(userInfoCopy);
    setUserSettings(userSettingsCopy);
    dispatch(setUsersPageView({ selectedUsersPageView: 'users' }));
  };

  return (
    <form className={styles.root} onSubmit={handleSubmit(saveProfile, focusFirstErrorField)}>
      <div className={styles.headerContainer} ref={headerRef}>
        <div className={styles.header}>
          <PageHeader
            label={'Users'}
            subLabel={`${selectedUser.firstName} ${selectedUser.lastName}`}
            onClick={() => {
              dispatch(setUsersPageView({ selectedUsersPageView: 'users' }));
            }}
          />
          <img
            alt={'avatar'}
            src={selectedUser?.avatarUrl || avatarNoIconBG}
            className={styles.smallAvatar}
          />
        </div>
        <div className={styles.actionButtons}>
          <CtaButton label={'Cancel'} size={'small'} onClick={cancelSave} />{' '}
          <CtaButton label={'Save Profile'} primary type="submit" />
        </div>
      </div>
      <ActionBar<View>
        forwardRef={tabRef}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={(tabButton, index) => {
          setView(tabButton.label);
          setSelectedTabIndex(index);
        }}
        tabButtons={tabs}
      />
      {currentTab(view)}
      {userData && !isUserAccountDataLoading && !userAccountDataError ? (
        <>
          <UserInfoForm
            control={control}
            errors={errors}
            isPersonaBasedManagementAllowed={isPersonaBasedManagementAllowed}
            isProductSettingsAdmin={isProductSettingsAdmin}
            isScimSettingEnabled={isScimSettingEnabled}
            setUnlinkPhoneNumber={setUnlinkPhoneNumber}
            selectedUser={selectedUser}
            userData={userData}
            userInfo={userInfo}
            userSettings={userSettings}
            setUserSettings={setUserSettings}
            setUserInfo={setUserInfo}
            isCustomAttributesFeatureFlagEnabled={isCustomAttributesFeatureFlagEnabled}
            isLocationsEnabled={isLocationsEnabled}
            isEditableEmailAddressEnabled={isEditableEmailAddressEnabled}
          />
          <UserSettingsForm
            isNewUserSettingsLayoutEnabled={isNewUserSettingsLayoutEnabled}
            userSettings={userSettings}
            setUserSettings={setUserSettings}
            setUserSettingsCopy={setUserSettingsCopy}
            userPermissions={userPermissions}
            setUserPermissions={setUserPermissions}
            setUserPermissionsCopy={setUserPermissionsCopy}
            selectedOrganization={selectedOrganization}
            selectedUser={selectedUser}
            userOrgSettings={orgSettings}
            setUserOrgSettings={setOrgSettings}
            urlParams={urlParams}
            featureFlags={featureFlags}
            userData={userData}
          />
        </>
      ) : (
        <div className={styles.loadingContainer}>
          <DotsIndicator />
        </div>
      )}
      <Toast
        type="SUCCESS"
        message="Account changes were successfully saved."
        open={isSuccessToastOpen}
        onClose={() => {
          setIsSuccessToastOpen(false);
        }}
      />
      <Toast
        type="FAILURE"
        message={
          failureToastMessage ??
          'There was a problem updating your account settings. Please try again.'
        }
        open={isFailureToastOpen}
        onClose={() => {
          setIsFailureToastOpen(false);
          setFailureToastMessage(undefined);
        }}
      />
      <ConfirmEmailChangeModal
        isOpen={isConfirmEmailModalOpen}
        closeModal={() => setIsConfirmEmailModalOpen(false)}
        options={{
          newEmail: userInfo.emailAddress ?? '',
          oldEmail: userInfoCopy.emailAddress ?? '',
          onConfirm: performSaveProfile,
          onCancel: () =>
            setUserInfo((prev) => ({ ...prev, emailAddress: userInfoCopy.emailAddress })),
        }}
      />
    </form>
  );
};

export default mobxInjectSelect<{}, MobxProps>({
  featureStore: ['featureFlags', 'getURLParams'],
  messengerStore: [
    'allowOneTimePasswordLogin',
    'isNewUserSettingsLayoutEnabled',
    'isProductSettingsAdmin',
    'isPersonaAdminFeatureFlagEnabled',
    'isCustomAttributesFeatureFlagEnabled',
    'isLocationsEnabled',
    'isEditableEmailAddressEnabled',
  ],
})(User);
